/**
 * ConsultaVozStreaming — modo voz EMR 100% manos libres.
 *
 * Captura mic (AirPods / Galaxy Buds Pro / cualquier BT) → PCM 16 kHz mono →
 * WebSocket /emr/voz/stream/{consulta_id} → transcripción + ruteo SOAP + TTS.
 *
 * Muestra transcripción en vivo, campos SOAP llenándose, indicador de estado
 * y reproduce audio TTS en los audífonos del médico.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, IconButton, Paper, Chip, LinearProgress, Tooltip,
  CircularProgress, Alert, Slide, Stack,
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  GraphicEq as WaveIcon,
  Headphones as HeadphonesIcon,
  RecordVoiceOver as SpeakerIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { auth } from '../../firebase';
import { StyledDialog } from '../common';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const WS_BASE = API_BASE.replace(/^http/, 'ws');

const TARGET_SR = 16000;
const CHUNK_MS = 250; // enviar cada 250 ms

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ────────────────────────────────────────────────────────────────
 * Hook de captura de mic → PCM int16 @ 16 kHz (funciona con AirPods,
 * Galaxy Buds Pro y cualquier mic seleccionado por el navegador).
 * ──────────────────────────────────────────────────────────────── */
function useMicPCMStream({ onChunk, onError }) {
  const [isRecording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const procRef = useRef(null);
  const sourceRef = useRef(null);
  const bufRef = useRef([]);
  const lastSendRef = useRef(0);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: 48000 });
      ctxRef.current = ctx;

      const src = ctx.createMediaStreamSource(stream);
      sourceRef.current = src;

      // ScriptProcessorNode sigue funcionando universalmente.
      // AudioWorklet se puede incorporar en una siguiente iteración.
      const bufSize = 4096;
      const proc = ctx.createScriptProcessor(bufSize, 1, 1);
      procRef.current = proc;

      proc.onaudioprocess = (ev) => {
        const input = ev.inputBuffer.getChannelData(0);
        // Downsample 48000 → 16000 (ratio 3) con promedio simple
        const ratio = ctx.sampleRate / TARGET_SR;
        const outLen = Math.floor(input.length / ratio);
        const pcm = new Int16Array(outLen);
        let sum = 0, count = 0, peak = 0;
        for (let i = 0; i < outLen; i++) {
          let acc = 0;
          const from = Math.floor(i * ratio);
          const to = Math.floor((i + 1) * ratio);
          for (let j = from; j < to; j++) acc += input[j] || 0;
          const v = acc / Math.max(1, to - from);
          sum += Math.abs(v);
          count++;
          const s16 = Math.max(-32768, Math.min(32767, Math.round(v * 32767)));
          pcm[i] = s16;
          if (Math.abs(v) > peak) peak = Math.abs(v);
        }
        bufRef.current.push(pcm);
        setLevel(peak);

        const now = performance.now();
        if (now - lastSendRef.current >= CHUNK_MS) {
          lastSendRef.current = now;
          const total = bufRef.current.reduce((s, a) => s + a.length, 0);
          const merged = new Int16Array(total);
          let off = 0;
          for (const a of bufRef.current) { merged.set(a, off); off += a.length; }
          bufRef.current = [];
          try { onChunk && onChunk(merged.buffer); }
          catch (e) { /* no-op */ }
        }
      };

      src.connect(proc);
      proc.connect(ctx.destination); // necesario en algunos navegadores
      setRecording(true);
    } catch (e) {
      onError && onError(e);
    }
  }, [onChunk, onError]);

  const stop = useCallback(() => {
    try { procRef.current && procRef.current.disconnect(); } catch {}
    try { sourceRef.current && sourceRef.current.disconnect(); } catch {}
    try { ctxRef.current && ctxRef.current.close(); } catch {}
    try {
      streamRef.current && streamRef.current.getTracks().forEach(t => t.stop());
    } catch {}
    procRef.current = null; sourceRef.current = null;
    ctxRef.current = null; streamRef.current = null;
    bufRef.current = []; setRecording(false); setLevel(0);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { isRecording, level, start, stop };
}

/* ────────────────────────────────────────────────────────────────
 * Panel SOAP en vivo
 * ──────────────────────────────────────────────────────────────── */
const CAMPO_LABELS = {
  motivo_consulta: 'Motivo de consulta',
  padecimiento_actual: 'Padecimiento actual',
  exploracion_fisica: 'Exploración física',
  signos_vitales: 'Signos vitales',
  plan_tratamiento: 'Plan',
  notas_evolucion: 'Nota de evolución',
  interrogatorio_aparatos_sistemas: 'Interrogatorio',
  diagnosticos: 'Diagnósticos',
  recetas: 'Recetas',
};

function CampoCard({ campo, valor, destacado }) {
  const label = CAMPO_LABELS[campo.split('.')[0]] || campo;
  return (
    <Paper
      elevation={destacado ? 4 : 0}
      sx={{
        p: 1.5, mb: 1, borderRadius: 2,
        border: '1px solid',
        borderColor: destacado ? '#10b981' : 'rgba(255,255,255,0.12)',
        background: destacado
          ? 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0.04) 100%)'
          : 'rgba(255,255,255,0.04)',
        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      <Typography variant="caption" sx={{
        color: destacado ? '#10b981' : '#64748b',
        fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10,
      }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#0f172a', fontSize: 13, mt: 0.5, wordBreak: 'break-word' }}>
        {typeof valor === 'object' ? (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {Object.entries(valor).map(([k, v]) => (
              <Chip key={k} size="small" label={`${k}: ${v}`}
                    sx={{ background: '#0ea5e920', color: '#0369a1', fontSize: 11 }} />
            ))}
          </Stack>
        ) : String(valor)}
      </Typography>
    </Paper>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Componente principal
 * ──────────────────────────────────────────────────────────────── */
export default function ConsultaVozStreaming({ open, onClose, consultaId }) {
  const [connected, setConnected] = useState(false);
  const [estadoSesion, setEstadoSesion] = useState('idle'); // idle|conectando|activa|pausada|esperando_confirmacion|cerrada|error
  const [transcripts, setTranscripts] = useState([]);
  const [campos, setCampos] = useState({});
  const [ultimoCampo, setUltimoCampo] = useState(null);
  const [ttsText, setTtsText] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [audioDevice, setAudioDevice] = useState('default');

  const wsRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const transcriptEndRef = useRef(null);

  const mic = useMicPCMStream({
    onChunk: (buf) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(buf);
      }
    },
    onError: (e) => {
      setErrorMsg('No se pudo acceder al micrófono: ' + e.message);
      setEstadoSesion('error');
    },
  });

  // Detectar label del dispositivo de audio (informativo)
  useEffect(() => {
    (async () => {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const input = devs.find(d => d.kind === 'audioinput' && d.deviceId === 'default');
        if (input && input.label) setAudioDevice(input.label);
      } catch {}
    })();
  }, [open]);

  // Reproduce audio TTS WAV
  const playTTS = useCallback(async (arrayBuffer) => {
    try {
      const blob = new Blob([arrayBuffer], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      if (!audioPlayerRef.current) audioPlayerRef.current = new Audio();
      const a = audioPlayerRef.current;
      a.src = url;
      a.onended = () => URL.revokeObjectURL(url);
      a.play().catch(() => {});
    } catch (e) { /* no-op */ }
  }, []);

  const connectAndStart = useCallback(async () => {
    setErrorMsg('');
    setTranscripts([]);
    setCampos({});
    setTtsText('');
    setEstadoSesion('conectando');

    try {
      const user = auth.currentUser;
      if (!user) {
        setErrorMsg('Sesión de Firebase no encontrada.');
        setEstadoSesion('error');
        return;
      }
      const token = await user.getIdToken();

      const ws = new WebSocket(`${WS_BASE}/emr/voz/stream/${consultaId}`);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        ws.send(JSON.stringify({
          type: 'auth',
          token,
          device: 'web',
          audio_device: audioDevice,
        }));
      };

      ws.onmessage = async (ev) => {
        if (typeof ev.data === 'string') {
          let msg;
          try { msg = JSON.parse(ev.data); } catch { return; }
          if (msg.type === 'transcript') {
            setTranscripts((prev) => [
              ...prev,
              { role: msg.speaker_role, text: msg.text, kind: msg.kind, ts: Date.now() },
            ]);
          } else if (msg.type === 'campo_actualizado') {
            setCampos((prev) => ({
              ...prev,
              [msg.campo]: msg.operacion === 'append'
                ? ((prev[msg.campo] || '') + ' ' + msg.valor).trim()
                : msg.operacion === 'merge' && typeof prev[msg.campo] === 'object'
                  ? { ...(prev[msg.campo] || {}), ...msg.valor }
                  : msg.valor,
            }));
            setUltimoCampo(msg.campo);
            setTimeout(() => setUltimoCampo(null), 1500);
          } else if (msg.type === 'tts_text') {
            setTtsText(msg.text);
          } else if (msg.type === 'state') {
            setEstadoSesion(msg.estado);
          } else if (msg.type === 'error') {
            setErrorMsg(msg.message || 'Error');
            setEstadoSesion('error');
          }
        } else {
          // Audio TTS
          await playTTS(ev.data);
        }
      };

      ws.onerror = () => {
        setErrorMsg('Error de conexión WebSocket.');
        setEstadoSesion('error');
      };

      ws.onclose = () => {
        setConnected(false);
        if (estadoSesion !== 'error') setEstadoSesion('cerrada');
        mic.stop();
      };

      // Arrancar mic después de abrir conexión
      await mic.start();
    } catch (e) {
      setErrorMsg('No se pudo iniciar: ' + e.message);
      setEstadoSesion('error');
    }
  }, [consultaId, audioDevice, mic, estadoSesion, playTTS]);

  const stopAll = useCallback(() => {
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'control', action: 'cerrar' }));
        wsRef.current.close();
      }
    } catch {}
    mic.stop();
    setConnected(false);
    setEstadoSesion('idle');
  }, [mic]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcripts.length]);

  // Cierre automático al cerrar el diálogo
  useEffect(() => {
    if (!open) stopAll();
  }, [open, stopAll]);

  const iconEstado = {
    idle: <HeadphonesIcon sx={{ fontSize: 20 }} />,
    conectando: <CircularProgress size={16} sx={{ color: '#fbbf24' }} />,
    activa: <MicIcon sx={{ fontSize: 20, color: '#10b981' }} />,
    pausada: <PauseIcon sx={{ fontSize: 20, color: '#f59e0b' }} />,
    esperando_confirmacion: <SpeakerIcon sx={{ fontSize: 20, color: '#fbbf24' }} />,
    cerrada: <CheckIcon sx={{ fontSize: 20, color: '#64748b' }} />,
    error: <MicOffIcon sx={{ fontSize: 20, color: '#ef4444' }} />,
  }[estadoSesion] || null;

  const labelEstado = {
    idle: 'Listo',
    conectando: 'Conectando...',
    activa: 'Escuchando',
    pausada: 'Pausada',
    esperando_confirmacion: 'Esperando confirmación',
    cerrada: 'Cerrada',
    error: 'Error',
  }[estadoSesion] || estadoSesion;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      title="Consulta por Voz"
      subtitle={`Consulta #${consultaId} — AirPods / Galaxy Buds Pro / cualquier auricular BT`}
      icon={<HeadphonesIcon />}
    >
      <Box sx={{ p: 2.5, minHeight: 520 }}>
        {/* Barra de estado */}
        <Paper sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          p: 1.5, mb: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#fff',
        }}>
          {iconEstado}
          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
            {labelEstado}
          </Typography>
          <Chip
            size="small"
            label={audioDevice.length > 40 ? audioDevice.slice(0, 38) + '...' : audioDevice}
            sx={{ background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 11 }}
          />
          <Box sx={{ flex: 1 }} />
          {mic.isRecording && (
            <Box sx={{ width: 120 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, mic.level * 300)}
                sx={{
                  height: 6, borderRadius: 3,
                  background: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': { background: '#10b981' },
                }}
              />
            </Box>
          )}
        </Paper>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
        )}

        {ttsText && (
          <Paper sx={{
            p: 1.5, mb: 2, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(139,92,246,0.04) 100%)',
            border: '1px solid #8b5cf640',
          }}>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <SpeakerIcon sx={{ color: '#8b5cf6', fontSize: 18, mt: 0.3 }} />
              <Typography sx={{ color: '#4c1d95', fontSize: 13, fontStyle: 'italic' }}>
                NARA: {ttsText}
              </Typography>
            </Stack>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Columna izquierda: transcripción */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{
              color: '#64748b', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.5, fontSize: 10,
            }}>
              Transcripción en vivo
            </Typography>
            <Box sx={{
              mt: 0.5, height: 380, overflowY: 'auto',
              p: 1.5, borderRadius: 2,
              background: 'rgba(248,250,252,0.8)',
              border: '1px solid #e2e8f0',
            }}>
              {transcripts.length === 0 && (
                <Typography sx={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic', textAlign: 'center', mt: 6 }}>
                  {mic.isRecording ? 'Escuchando...' : 'Toca el micrófono para iniciar'}
                </Typography>
              )}
              {transcripts.map((t, i) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <Chip
                    size="small"
                    label={t.role === 'medico' ? 'Médico' : t.role === 'paciente' ? 'Paciente' : t.role}
                    sx={{
                      height: 18, fontSize: 10, fontWeight: 700,
                      background: t.role === 'medico' ? '#0ea5e920' : '#8b5cf620',
                      color: t.role === 'medico' ? '#0369a1' : '#7c3aed',
                      mr: 1,
                    }}
                  />
                  {t.kind === 'comando' && (
                    <Chip size="small" label="comando" sx={{ height: 18, fontSize: 10, background: '#fbbf24', mr: 1 }} />
                  )}
                  <Typography component="span" sx={{ fontSize: 13, color: '#0f172a' }}>
                    {t.text}
                  </Typography>
                </Box>
              ))}
              <div ref={transcriptEndRef} />
            </Box>
          </Box>

          {/* Columna derecha: campos SOAP */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{
              color: '#64748b', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.5, fontSize: 10,
            }}>
              Expediente — actualizándose
            </Typography>
            <Box sx={{ mt: 0.5, height: 380, overflowY: 'auto', pr: 0.5 }}>
              {Object.keys(campos).length === 0 && (
                <Typography sx={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic', textAlign: 'center', mt: 6 }}>
                  Los campos se llenarán automáticamente mientras hablas.
                </Typography>
              )}
              {Object.entries(campos).map(([k, v]) => (
                <CampoCard
                  key={k}
                  campo={k}
                  valor={v}
                  destacado={ultimoCampo === k}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Controles */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2.5 }}>
          {!mic.isRecording ? (
            <Tooltip title="Iniciar consulta por voz">
              <IconButton
                size="large"
                onClick={connectAndStart}
                disabled={estadoSesion === 'conectando'}
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff', width: 72, height: 72,
                  boxShadow: '0 8px 24px rgba(16,185,129,0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    transform: 'scale(1.04)',
                  },
                  '&:disabled': { background: '#cbd5e1', color: '#fff' },
                  transition: 'all 0.2s',
                }}
              >
                <MicIcon sx={{ fontSize: 36 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Detener">
              <IconButton
                size="large"
                onClick={stopAll}
                sx={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: '#fff', width: 72, height: 72,
                  boxShadow: '0 8px 24px rgba(239,68,68,0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    transform: 'scale(1.04)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <MicOffIcon sx={{ fontSize: 36 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Typography sx={{
          mt: 1.5, textAlign: 'center', fontSize: 11,
          color: '#64748b', fontStyle: 'italic',
        }}>
          Tip: di <b>"Nara, ..."</b> para dar comandos (agregar diagnóstico,
          prescribir, signos vitales, firmar y cerrar).
        </Typography>
      </Box>
    </StyledDialog>
  );
}
